import type { PropsWithChildren } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import useActiveVouchers from 'marketing/hooks/useActiveVouchers'

type ActiveVouchersContextValue = {
  error: unknown | null
  isLoading: boolean
  total: number
  vouchers: Array<App.PublicVoucher>
}

type ActiveVouchersProviderValue = ActiveVouchersContextValue & {
  registerConsumer: () => () => void
}

type ActiveVouchersProviderProps = PropsWithChildren<{
  prefetch?: boolean
}>

type UseActiveVouchersContextOptions = {
  enabled?: boolean
}

const EMPTY_VOUCHERS: Array<App.PublicVoucher> = []
const EMPTY_ACTIVE_VOUCHERS_CONTEXT: ActiveVouchersContextValue = {
  error: null,
  isLoading: false,
  total: 0,
  vouchers: EMPTY_VOUCHERS,
}

const ActiveVouchersContext = createContext<ActiveVouchersProviderValue | undefined>(undefined)

function ActiveVouchersProvider({ children, prefetch = false }: ActiveVouchersProviderProps) {
  const [consumerCount, setConsumerCount] = useState(0)
  const registerConsumer = useCallback(() => {
    setConsumerCount(count => count + 1)

    return () => setConsumerCount(count => Math.max(0, count - 1))
  }, [])
  const query = useActiveVouchers({ enabled: prefetch || consumerCount > 0 })
  const value = useMemo<ActiveVouchersProviderValue>(() => ({
    error: query.error,
    isLoading: query.isLoading,
    registerConsumer,
    total: query.data?.total ?? 0,
    vouchers: query.data?.vouchers ?? EMPTY_VOUCHERS,
  }), [query.data, query.error, query.isLoading, registerConsumer])

  return <ActiveVouchersContext value={value}>{children}</ActiveVouchersContext>
}

function useActiveVouchersContext(options: UseActiveVouchersContextOptions = {}): ActiveVouchersContextValue {
  const context = useContext(ActiveVouchersContext)
  const registerConsumer = context?.registerConsumer
  const enabled = options.enabled ?? true

  useEffect(() => {
    if (!enabled || !registerConsumer) return

    return registerConsumer()
  }, [enabled, registerConsumer])

  if (context) return context

  if (process.env.NODE_ENV !== 'production') {
    console.error(
      'ActiveVouchersProvider is missing. Active voucher features will use an empty state.',
    )
  }

  return EMPTY_ACTIVE_VOUCHERS_CONTEXT
}

export { ActiveVouchersProvider, useActiveVouchersContext }
